import React, { useEffect, useState } from 'react';
import ModalShell from '../ui/ModalShell';
import { ShieldCheck, Upload as UploadIcon, Plus, Search, Loader2 } from 'lucide-react';
import { SHEET_TOKENS, getGlassSurface, getTextColors, getInnerCardSurface } from '../../pages/MojeSmena/sheetStyle';
import { SheetButton } from '../../pages/MojeSmena/SheetUI';

// POZOR: Field MUSÍ být mimo parent — definovat dovnitř způsobí unmount/remount
// na každý render → focus skáče. (viz TankovaniModal komentář.)
function Field({ label, children, required, labelColor }) {
    return (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{
                fontSize: 10, fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: 0.5,
                color: labelColor,
            }}>
                {label}{required && <span style={{ color: '#EF4444' }}> *</span>}
            </span>
            {children}
        </label>
    );
}

/**
 * Modal pro zaznamenání pojistné smlouvy konkrétnímu vozidlu. Vyvolává se
 * z Inspector → Pojištění sub-tab. Vozidlo je pre-fillované (single-vehicle
 * režim — žádný dropdown na výběr auta jako u staré globální verze).
 *
 * Volitelně podporuje upload PDF/scan přes `/pojisteni/{id}/upload` po
 * úspěšném `store`.
 */
export default function PojistniModal({ vozidlo, onClose, onSaved, isDark, isMobile = false }) {
    const [form, setForm] = useState({
        typ: 'pojisteni',
        pojisteni_typ: 'pov',
        dodavatel_id: '',
        cislo_smlouvy: '',
        rocni_pojistne_kc: '',
        platnost_od: '',
        platnost_do: '',
    });
    const [pojistovny, setPojistovny] = useState([]);
    const [novaPoj, setNovaPoj] = useState(null); // null | { ico, nazev, adresa, lookupLoading, lookupError, saving }
    const [saving, setSaving] = useState(false);
    const [file, setFile] = useState(null);
    const [error, setError] = useState(null);

    const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content || '';

    const loadPojistovny = () => {
        return fetch('/api/dodavatele?kategorie=pojistovna&aktivni=1')
            .then((r) => r.json())
            .then((d) => { if (Array.isArray(d)) setPojistovny(d); return Array.isArray(d) ? d : []; })
            .catch(() => []);
    };
    useEffect(() => { loadPojistovny(); }, []);

    const aresLookup = async () => {
        if (!novaPoj?.ico) return;
        setNovaPoj((p) => ({ ...p, lookupLoading: true, lookupError: null }));
        try {
            const r = await fetch(`/api/dodavatele/ares-lookup?ico=${encodeURIComponent(novaPoj.ico)}`, { credentials: 'same-origin' });
            const data = await r.json();
            if (!r.ok) throw new Error(data?.error || 'ARES lookup selhal');
            setNovaPoj((p) => ({
                ...p, lookupLoading: false, lookupError: null,
                nazev: data.nazev || p.nazev || '',
                adresa: data.adresa || p.adresa || '',
            }));
        } catch (err) {
            setNovaPoj((p) => ({ ...p, lookupLoading: false, lookupError: err.message }));
        }
    };

    const ulozitNovouPojistovnu = async () => {
        if (!novaPoj?.nazev?.trim()) {
            setNovaPoj((p) => ({ ...p, lookupError: 'Název je povinný' }));
            return;
        }
        setNovaPoj((p) => ({ ...p, saving: true, lookupError: null }));
        try {
            const r = await fetch('/api/dodavatele', {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrf(), 'Accept': 'application/json' },
                body: JSON.stringify({
                    nazev: novaPoj.nazev.trim(),
                    ico: novaPoj.ico || null,
                    adresa: novaPoj.adresa || null,
                    kategorie: ['pojistovna'],
                    aktivni: true,
                }),
            });
            const data = await r.json();
            if (!r.ok) {
                const firstErr = data?.errors ? Object.values(data.errors)[0]?.[0] : null;
                throw new Error(firstErr || data?.error || 'Uložení selhalo');
            }
            await loadPojistovny();
            setForm((f) => ({ ...f, dodavatel_id: String(data.id) }));
            setNovaPoj(null);
        } catch (err) {
            setNovaPoj((p) => ({ ...p, saving: false, lookupError: err.message }));
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        try {
            const headers = {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': csrf(),
                'Accept': 'application/json',
            };
            const payload = {
                ...form,
                vozidlo_id: vozidlo.id,
                dodavatel_id: form.dodavatel_id ? Number(form.dodavatel_id) : null,
            };
            const r = await fetch('/api/flotila/pojisteni', {
                method: 'POST',
                headers,
                credentials: 'same-origin',
                body: JSON.stringify(payload),
            });
            if (!r.ok) {
                const data = await r.json().catch(() => ({}));
                const firstErr = data?.errors ? Object.values(data.errors)[0]?.[0] : null;
                throw new Error(firstErr || data?.message || data?.error || 'Uložení selhalo');
            }
            const saved = await r.json();
            if (file && saved.id) {
                const fd = new FormData();
                fd.append('soubor', file);
                await fetch(`/api/flotila/pojisteni/${saved.id}/upload`, {
                    method: 'POST',
                    credentials: 'same-origin',
                    headers: { 'X-CSRF-TOKEN': headers['X-CSRF-TOKEN'], 'Accept': 'application/json' },
                    body: fd,
                });
            }
            onSaved?.(saved);
            onClose?.();
        } catch (err) {
            setError(err.message || 'Uložení selhalo');
        } finally {
            setSaving(false);
        }
    };

    const _colors = getTextColors(isDark);
    const _inner = getInnerCardSurface(isDark);
    const labelColor = _colors.textMuted;
    const subtle = _colors.textSecondary;
    const textColor = _colors.textPrimary;

    const inputStyle = {
        width: '100%',
        height: SHEET_TOKENS.sizes.inputHeight,
        background: _inner.background,
        color: textColor,
        border: `1px solid ${_inner.border.split(' ').pop()}`,
        borderRadius: SHEET_TOKENS.radius.input,
        padding: '0 12px',
        fontSize: SHEET_TOKENS.typography.body.size,
        fontWeight: SHEET_TOKENS.typography.body.weight,
        outline: 'none',
        fontFamily: 'inherit',
        boxSizing: 'border-box',
    };
    const monoInput = { ...inputStyle, fontVariantNumeric: 'tabular-nums' };
    const optionStyle = {
        background: isDark ? '#1a1a1a' : '#fff',
        color: isDark ? '#fff' : '#111',
    };

    const podtitul = [vozidlo?.spz, vozidlo?.model].filter(Boolean).join(' · ');
    return (
        <ModalShell onClose={onClose} isDark={isDark} ikona={ShieldCheck} barva="#10B981" nadpis="Nová pojistná smlouva" sirka={480}>
            <form onSubmit={submit} style={{
                padding: SHEET_TOKENS.spacing.sheetPadX, display: 'flex', flexDirection: 'column', gap: 12, color: textColor,
            }}>
                {podtitul && <p style={{ margin: 0, fontSize: 12, color: labelColor }}>{podtitul}</p>}

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                        <Field label="Typ smlouvy" required labelColor={labelColor}>
                            <select className="cockpit-input" style={inputStyle} value={form.typ} onChange={change('typ')}>
                                <option value="pojisteni" style={optionStyle}>Pojištění</option>
                                <option value="zelena_karta" style={optionStyle}>Zelená karta</option>
                            </select>
                        </Field>
                        {form.typ === 'pojisteni' && (
                            <Field label="Pojištění typ" required labelColor={labelColor}>
                                <select className="cockpit-input" style={inputStyle} value={form.pojisteni_typ} onChange={change('pojisteni_typ')}>
                                    <option value="pov" style={optionStyle}>POV</option>
                                    <option value="hav" style={optionStyle}>HAV</option>
                                    <option value="gap" style={optionStyle}>GAP</option>
                                    <option value="jine" style={optionStyle}>Jiné</option>
                                </select>
                            </Field>
                        )}
                    </div>

                    {form.typ === 'pojisteni' && (
                        <>
                            <Field label="Pojišťovna" required labelColor={labelColor}>
                                <div style={{ display: 'flex', gap: 6 }}>
                                    <select
                                        style={{ ...inputStyle, flex: 1 }}
                                        value={form.dodavatel_id}
                                        onChange={change('dodavatel_id')}
                                        required
                                    >
                                        <option value="" style={optionStyle}>— vyber pojišťovnu —</option>
                                        {pojistovny.map((p) => (
                                            <option key={p.id} value={p.id} style={optionStyle}>{p.nazev}</option>
                                        ))}
                                    </select>
                                    <button
                                        type="button"
                                        onClick={() => setNovaPoj({ ico: '', nazev: '', adresa: '', lookupLoading: false, lookupError: null, saving: false })}
                                        title="Přidat novou pojišťovnu"
                                        style={{
                                            background: 'rgba(16,185,129,0.10)',
                                            color: '#10B981',
                                            border: '1px solid rgba(16,185,129,0.30)',
                                            borderRadius: 7, padding: '0 10px',
                                            fontSize: 13, fontWeight: 700, cursor: 'pointer',
                                            display: 'inline-flex', alignItems: 'center', gap: 4,
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        <Plus size={13} /> Nová
                                    </button>
                                </div>
                            </Field>

                            {novaPoj && (
                                <div style={{
                                    padding: 12, borderRadius: 9,
                                    background: isDark ? 'rgba(16,185,129,0.06)' : 'rgba(16,185,129,0.04)',
                                    border: `1px dashed ${isDark ? 'rgba(16,185,129,0.30)' : 'rgba(16,185,129,0.40)'}`,
                                    display: 'flex', flexDirection: 'column', gap: 8,
                                }}>
                                    <div style={{
                                        fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
                                        color: '#10B981',
                                    }}>
                                        Nová pojišťovna
                                    </div>
                                    <div style={{ display: 'flex', gap: 6 }}>
                                        <input
                                            style={{ ...inputStyle, fontVariantNumeric: 'tabular-nums', flex: 1 }}
                                            inputMode="numeric"
                                            placeholder="IČO (volitelně)"
                                            value={novaPoj.ico}
                                            onChange={(e) => setNovaPoj((p) => ({ ...p, ico: e.target.value.replace(/\D/g, '').slice(0, 8) }))}
                                        />
                                        <button
                                            type="button"
                                            onClick={aresLookup}
                                            disabled={!novaPoj.ico || novaPoj.lookupLoading || novaPoj.ico.length !== 8}
                                            style={{
                                                background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                                                color: textColor,
                                                border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}`,
                                                borderRadius: 7, padding: '0 10px',
                                                fontSize: 12, fontWeight: 600,
                                                cursor: (!novaPoj.ico || novaPoj.lookupLoading) ? 'not-allowed' : 'pointer',
                                                opacity: (!novaPoj.ico || novaPoj.lookupLoading) ? 0.55 : 1,
                                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {novaPoj.lookupLoading
                                                ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
                                                : <Search size={12} />}
                                            ARES
                                        </button>
                                    </div>
                                    <input
                                        className="cockpit-input" style={inputStyle}
                                        placeholder="Název *"
                                        value={novaPoj.nazev}
                                        onChange={(e) => setNovaPoj((p) => ({ ...p, nazev: e.target.value }))}
                                        required
                                    />
                                    <input
                                        className="cockpit-input" style={inputStyle}
                                        placeholder="Adresa (volitelně)"
                                        value={novaPoj.adresa}
                                        onChange={(e) => setNovaPoj((p) => ({ ...p, adresa: e.target.value }))}
                                    />
                                    {novaPoj.lookupError && (
                                        <div style={{
                                            fontSize: 11, color: '#EF4444',
                                            padding: '4px 8px', borderRadius: 5,
                                            background: 'rgba(239,68,68,0.10)',
                                        }}>
                                            {novaPoj.lookupError}
                                        </div>
                                    )}
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
                                        <button
                                            type="button"
                                            onClick={() => setNovaPoj(null)}
                                            style={{
                                                background: 'transparent', color: subtle,
                                                border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}`,
                                                padding: '6px 12px', borderRadius: 6,
                                                fontSize: 12, fontWeight: 600, cursor: 'pointer',
                                            }}
                                        >
                                            Zrušit
                                        </button>
                                        <button
                                            type="button"
                                            onClick={ulozitNovouPojistovnu}
                                            disabled={novaPoj.saving}
                                            style={{
                                                background: '#10B981',
                                                color: '#fff', border: 'none',
                                                padding: '6px 14px', borderRadius: 6,
                                                fontSize: 12, fontWeight: 700,
                                                cursor: novaPoj.saving ? 'wait' : 'pointer',
                                                opacity: novaPoj.saving ? 0.6 : 1,
                                            }}
                                        >
                                            {novaPoj.saving ? 'Ukládám…' : 'Uložit pojišťovnu'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            <Field label="Číslo smlouvy" required labelColor={labelColor}>
                                <input className="cockpit-input" style={monoInput} value={form.cislo_smlouvy}
                                    onChange={change('cislo_smlouvy')} required />
                            </Field>
                            <Field label="Roční pojistné (Kč)" labelColor={labelColor}>
                                <input className="cockpit-input" style={monoInput} type="number" step="0.01"
                                    value={form.rocni_pojistne_kc} onChange={change('rocni_pojistne_kc')} />
                            </Field>
                        </>
                    )}

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                        <Field label="Platnost od" required labelColor={labelColor}>
                            <input className="cockpit-input" style={inputStyle} type="date"
                                value={form.platnost_od} onChange={change('platnost_od')} required />
                        </Field>
                        <Field label="Platnost do" required labelColor={labelColor}>
                            <input className="cockpit-input" style={inputStyle} type="date"
                                value={form.platnost_do} onChange={change('platnost_do')}
                                min={form.platnost_od} required />
                        </Field>
                    </div>

                    <Field
                        label={<span><UploadIcon size={11} style={{ display: 'inline', marginRight: 4, verticalAlign: 'middle' }} />PDF / scan smlouvy (volitelně)</span>}
                        labelColor={labelColor}
                    >
                        <input className="cockpit-input" style={inputStyle} type="file" accept=".pdf,.jpg,.jpeg,.png"
                            onChange={(e) => setFile(e.target.files?.[0] || null)} />
                    </Field>

                    {error && (
                        <div style={{
                            padding: '8px 12px', borderRadius: 7, fontSize: 12,
                            background: 'rgba(239,68,68,0.12)', color: '#EF4444',
                            border: '1px solid rgba(239,68,68,0.30)',
                        }}>
                            {error}
                        </div>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
                        <SheetButton
                            isDark={isDark}
                            variant="secondary"
                            onClick={onClose}
                            fullWidth={false}
                        >Zrušit</SheetButton>
                        <SheetButton
                            isDark={isDark}
                            variant="primary"
                            accent="#1D9E75"
                            accent2="#10B981"
                            type="submit"
                            busy={saving}
                            busyLabel="Ukládám…"
                            fullWidth={false}
                        >Uložit smlouvu</SheetButton>
                    </div>
            </form>
        </ModalShell>
    );
}
